import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useInterval } from "../../../../lib/src/util/useInterval";
import { DeploymentStatus } from "../../../../lib/src/api/common/models/DeploymentStatus";
import { Deployment } from "../../../../lib/src/api/partner/models/Deployment";

interface RealtimeDeploymentProviderType {
  deploymentInProgress: boolean
  deployments: Deployment[]
  runningDeployments: Deployment[]
  deploymentFailed: boolean
  failedDeployments: Deployment[]
  getRealtimeStatus(entityId: string, fallbackStatus: DeploymentStatus): DeploymentStatus
  reload: () => void
  awaitNextDeploymentFinished(entityId: string): Promise<Deployment | null>
}

// eslint-disable-next-line react-refresh/only-export-components -- colocated with its RealtimeDeploymentProvider
export const RealtimeDeploymentContext = createContext<RealtimeDeploymentProviderType | null>(null);

interface RealtimeDeploymentProviderProps {
  children: ReactNode
  resolveDeployments: () => Promise<Deployment[]>
}
export const RealtimeDeploymentProvider = ({ children, resolveDeployments }: RealtimeDeploymentProviderProps) => {
  const [refreshEnabled] = useState(true)
  const [deployments, setDeployments] = useState<Deployment[]>([])

  const load = useCallback(async () => {
    const response = await resolveDeployments()
    setDeployments(response)
  }, [resolveDeployments])

  useInterval(() => load(), refreshEnabled ? 2000 : null)

  const runningDeployments = useMemo(() => {
    return deployments.filter(d => d.status === "running" || d.status === "pending")
  }, [deployments])

  const failedDeployments = useMemo(() => {
    return deployments.filter(d => d.status === "failed")
  }, [deployments])

  const getStatus = useCallback((entityId: string, fallbackStatus: DeploymentStatus) => {
    const realtimeDeployment = deployments.find(d => d.entityId === entityId)
    if (fallbackStatus === "success" && realtimeDeployment?.status === "failed") {
      // This case covers a deployment that has failed in the past.
      // TODO: Remove this once it is fixed in the backend.
      return "success"
    }
    return realtimeDeployment?.status ?? fallbackStatus
  }, [deployments])


  /**
   * This part may seem a little complicated. It allows the user to wait for the next deployment to finish based on an entityId.
   * This works by registering a callback that gets called when a deployment finishes with a given entityId.
   * To prevent false positives when the deployment hasn't started yet, all deployments are registered in a history.
   * When a deployment finishes, all deployments with the given entityId are removed from the history and the callback is called.
   * This last part is important to prevent false positives when a second deployment is dispatched for the entity.
   */
  const [deploymentHistory, setDeploymentHistory] = useState<Deployment[]>([])
  const [deploymentCallbacks, setDeploymentCallbacks] = useState<Record<string, (deployment: Deployment | null) => void>>({})
  const awaitNextDeploymentFinished = useCallback(async (entityId: string) => {
    return new Promise<Deployment | null>((resolve) => {
      setDeploymentCallbacks(old => ({
        ...old,
        [entityId]: (deployment: Deployment | null) => {
          setDeploymentCallbacks(old => {
            return Object.fromEntries(Object.entries(old).filter(([key]) => key !== entityId))
          })
          resolve(deployment)
        }
      }))
    })
  }, [])
  useEffect(() => {
    // Upsert into history so a deployment is still resolvable via
    // awaitNextDeploymentFinished after it ages off the polled page.
    // While it's still present in `deployments`, the matching effect
    // below reads that directly instead of this (deliberately, to avoid
    // a same-render staleness race between these two effects).
    if (deployments.length === 0) return
    setDeploymentHistory(old => {
      const byId = new Map(old.map(d => [d.id, d]))
      let changed = false
      for (const d of deployments) {
        if (byId.get(d.id) !== d) {
          byId.set(d.id, d)
          changed = true
        }
      }
      return changed ? Array.from(byId.values()) : old
    })
  }, [deployments]);
  useEffect(() => {
    Object.entries(deploymentCallbacks).forEach(([key, callback]) => {
      // Prefer the freshly-polled value (this render's `deployments`) over
      // `deploymentHistory`, which may still hold a stale pre-transition
      // snapshot from the sibling effect above at this point in the render.
      const found = deployments.find(d => d.id === key || d.entityId === key)
        ?? deploymentHistory.find(d => d.id === key || d.entityId === key)
      if (!found || found.status === 'running' || found.status === 'pending') {
        return
      }
      setDeploymentHistory(old => old.filter(d => d.id !== found.id))
      callback(found)
    })
  }, [deployments, deploymentCallbacks, deploymentHistory]);

  return <RealtimeDeploymentContext.Provider value={{
    runningDeployments,
    deploymentInProgress: runningDeployments.length > 0,
    failedDeployments,
    deploymentFailed: failedDeployments.length > 0,
    getRealtimeStatus: getStatus,
    deployments,
    reload: load,
    awaitNextDeploymentFinished,
  }}>{children}</RealtimeDeploymentContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- colocated with its RealtimeDeploymentProvider
export const useRealtimeDeployments = (): RealtimeDeploymentProviderType | null => {
  return useContext(RealtimeDeploymentContext)
}
